import axios from "axios";

import { attachOfflineInterceptor } from "@/lib/offlineQueue";

// Always use relative URL - works in both dev and production
const API_URL = '/api';

// Auth is handled via secure HttpOnly cookies set by the backend.
// Ensure cookies are always sent with requests (same-origin + cross-origin).
axios.defaults.withCredentials = true;

// Löst einen Datei-Download aus einem Blob-Response aus.
export const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const placetelFilename = () => `Placetel-Export-${new Date().toISOString().slice(0, 10)}.csv`;
export const PLACETEL_HINT = "Datei in Placetel unter Telefonbuch > Importieren hochladen.";

// Customers API
// Kunden
export const customersApi = {
  getAll: (search) => axios.get(`${API_URL}/customers`, { params: { search } }),
  getAllLite: (search) => axios.get(`${API_URL}/customers`, { params: { search, lite: true } }),
  getPaged: (params) => axios.get(`${API_URL}/customers/paged`, { params }),
  bulkDelete: (customerIds) => axios.post(`${API_URL}/customers/bulk-delete`, { customer_ids: customerIds }),
  getById: (id) => axios.get(`${API_URL}/customers/${id}`),
  create: (data) => axios.post(`${API_URL}/customers`, data),
  update: (id, data, expectedUpdatedAt) =>
    axios.put(`${API_URL}/customers/${id}`, data, expectedUpdatedAt ? { params: { expected_updated_at: expectedUpdatedAt } } : undefined),
  delete: (id) => axios.delete(`${API_URL}/customers/${id}`),
  getCategories: () => axios.get(`${API_URL}/customers/categories`),
  getWithReminders: () => axios.get(`${API_URL}/customers/with-reminders`),
  bulkUpdate: (customerIds, category, hasMaintenance) => 
    axios.post(`${API_URL}/customers/bulk-update`, { 
      customer_ids: customerIds, 
      category, 
      has_maintenance_contract: hasMaintenance 
    }),
  exportCsv: () => axios.get(`${API_URL}/customers/export/csv`, { responseType: 'blob' }),
  exportPlacetel: (params) => axios.get(`${API_URL}/customers/export/placetel`, { params, responseType: 'blob' }),
  exportCustomerPlacetel: (id, params) => axios.get(`${API_URL}/customers/${id}/export/placetel`, { params, responseType: 'blob' }),
  importCsv: (file, updateExisting = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/customers/import/csv?update_existing=${updateExisting}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  cleanupHighlights: () => axios.post(`${API_URL}/customers/cleanup-highlights`),
  cleanupNotes: () => axios.post(`${API_URL}/customers/cleanup-notes`),
  addConversationNote: (id, data) => axios.post(`${API_URL}/customers/${id}/conversation-notes`, data),
  updateConversationNote: (id, entryId, data) => axios.put(`${API_URL}/customers/${id}/conversation-notes/${entryId}`, data),
  deleteConversationNote: (id, entryId) => axios.delete(`${API_URL}/customers/${id}/conversation-notes/${entryId}`),
  getDraft: () => axios.get(`${API_URL}/customers/draft`),
  saveDraft: (data) => axios.put(`${API_URL}/customers/draft`, { data }),
  deleteDraft: () => axios.delete(`${API_URL}/customers/draft`),
};

// Per-user table preferences & saved views (Phase 2 unified tables)
export const tableViewsApi = {
  get: (tableKey) => axios.get(`${API_URL}/table-views/${tableKey}`),
  savePrefs: (tableKey, prefs) => axios.put(`${API_URL}/table-views/${tableKey}/prefs`, { prefs }),
  addView: (tableKey, name, config) => axios.post(`${API_URL}/table-views/${tableKey}/views`, { name, config }),
  deleteView: (tableKey, viewId) => axios.delete(`${API_URL}/table-views/${tableKey}/views/${viewId}`),
};

// Customer Documents API
export const customerDocumentsApi = {
  getAll: (customerId) => axios.get(`${API_URL}/customers/${customerId}/documents`),
  upload: (customerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/customers/${customerId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  download: (customerId, documentId) => 
    axios.get(`${API_URL}/customers/${customerId}/documents/${documentId}/download`, { responseType: 'blob' }),
  delete: (customerId, documentId) => 
    axios.delete(`${API_URL}/customers/${customerId}/documents/${documentId}`),
};

// Task Attachments API (Object Storage)
export const taskAttachmentsApi = {
  list: (taskId) => axios.get(`${API_URL}/tasks/${taskId}/attachments`),
  upload: (file, taskId) => {
    const formData = new FormData();
    formData.append("file", file);
    if (taskId) formData.append("task_id", taskId);
    return axios.post(`${API_URL}/task-attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  delete: (attachmentId) => axios.delete(`${API_URL}/task-attachments/${attachmentId}`),
  downloadUrl: (attachmentId) => `${API_URL}/task-attachments/${attachmentId}/download`,
};

// Service Report Attachments API (Object Storage)
export const serviceReportAttachmentsApi = {
  list: (reportId) => axios.get(`${API_URL}/service-reports/${reportId}/attachments`),
  upload: (file, reportId) => {
    const formData = new FormData();
    formData.append("file", file);
    if (reportId) formData.append("report_id", reportId);
    return axios.post(`${API_URL}/service-report-attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  delete: (attachmentId) => axios.delete(`${API_URL}/service-report-attachments/${attachmentId}`),
  downloadUrl: (attachmentId) => `${API_URL}/service-report-attachments/${attachmentId}/download`,
};

// Serial Numbers API
export const serialNumbersApi = {
  getAll: (search, customerId) => axios.get(`${API_URL}/serial-numbers`, { params: { search, customer_id: customerId } }),
  getPaged: (params) => axios.get(`${API_URL}/serial-numbers/paged`, { params }),
  bulkDelete: (ids) => axios.post(`${API_URL}/serial-numbers/bulk-delete`, { ids }),
  getById: (id) => axios.get(`${API_URL}/serial-numbers/${id}`),
  create: (data) => axios.post(`${API_URL}/serial-numbers`, data),
  update: (id, data, expectedUpdatedAt) =>
    axios.put(`${API_URL}/serial-numbers/${id}`, data, expectedUpdatedAt ? { params: { expected_updated_at: expectedUpdatedAt } } : undefined),
  delete: (id) => axios.delete(`${API_URL}/serial-numbers/${id}`),
  getExpiring: () => axios.get(`${API_URL}/serial-numbers/reminders/expiring`),
  getCategories: () => axios.get(`${API_URL}/serial-numbers/categories`),
  getStatistics: () => axios.get(`${API_URL}/serial-numbers/statistics`),
  getCalendar: () => axios.get(`${API_URL}/serial-numbers/calendar`),
};

// Reminder API
export const reminderApi = {
  sendEmail: (serialId, note) => axios.post(`${API_URL}/reminders/send-email`, { serial_id: serialId, note }),
  sendDailySummary: () => axios.post(`${API_URL}/reminders/send-daily-summary`),
  getCalendarUrl: (serialId) => `${API_URL}/reminders/calendar/${serialId}`,
  getSmtpSettings: () => axios.get(`${API_URL}/settings/smtp`),
  updateSmtpSettings: (data) => axios.put(`${API_URL}/settings/smtp`, data),
  testSmtpSettings: () => axios.post(`${API_URL}/settings/smtp/test`),
};

// Service Reports API
export const serviceReportsApi = {
  getAll: (search, customerId) => axios.get(`${API_URL}/service-reports`, { params: { search, customer_id: customerId } }),
  getPaged: (params) => axios.get(`${API_URL}/service-reports/paged`, { params }),
  bulkDelete: (ids) => axios.post(`${API_URL}/service-reports/bulk-delete`, { ids }),
  getById: (id) => axios.get(`${API_URL}/service-reports/${id}`),
  create: (data) => axios.post(`${API_URL}/service-reports`, data),
  update: (id, data, expectedUpdatedAt) =>
    axios.put(`${API_URL}/service-reports/${id}`, data, expectedUpdatedAt ? { params: { expected_updated_at: expectedUpdatedAt } } : undefined),
  delete: (id) => axios.delete(`${API_URL}/service-reports/${id}`),
  getPdf: (id) => axios.get(`${API_URL}/service-reports/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id, data) => axios.post(`${API_URL}/service-reports/${id}/send-email`, data),
  getDraft: () => axios.get(`${API_URL}/service-reports/draft`),
  saveDraft: (data) => axios.put(`${API_URL}/service-reports/draft`, { data }),
  deleteDraft: () => axios.delete(`${API_URL}/service-reports/draft`),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => axios.get(`${API_URL}/dashboard/stats`),
  getActivity: () => axios.get(`${API_URL}/dashboard/activity`),
};

// Knowledge Base API
export const knowledgeApi = {
  getCategories: () => axios.get(`${API_URL}/knowledge/categories`),
  createCategory: (name) => axios.post(`${API_URL}/knowledge/categories`, { name }),
  deleteCategory: (name) => axios.delete(`${API_URL}/knowledge/categories/${encodeURIComponent(name)}`),
  getArticles: (search, category) => axios.get(`${API_URL}/knowledge/articles`, { params: { search, category } }),
  getArticle: (id) => axios.get(`${API_URL}/knowledge/articles/${id}`),
  createArticle: (data) => axios.post(`${API_URL}/knowledge/articles`, data),
  updateArticle: (id, data) => axios.put(`${API_URL}/knowledge/articles/${id}`, data),
  deleteArticle: (id) => axios.delete(`${API_URL}/knowledge/articles/${id}`),
  reorderArticles: (items) => axios.post(`${API_URL}/knowledge/articles/reorder`, { items }),
  reorderCategories: (items) => axios.post(`${API_URL}/knowledge/categories/reorder`, { items }),
  listOrphanImages: () => axios.get(`${API_URL}/knowledge/images-manage/orphans`),
  deleteImage: (id) => axios.delete(`${API_URL}/knowledge/images/${id}`),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios.post(`${API_URL}/knowledge/images`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  getVersions: (id) => axios.get(`${API_URL}/knowledge/articles/${id}/versions`),
  restoreVersion: (id, version) => axios.post(`${API_URL}/knowledge/articles/${id}/versions/${version}/restore`),
  getAttachments: (articleId) => axios.get(`${API_URL}/knowledge/articles/${articleId}/attachments`),
  uploadAttachment: (articleId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/knowledge/articles/${articleId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadAttachment: (attachmentId) => 
    axios.get(`${API_URL}/knowledge/attachments/${attachmentId}/download`, { responseType: 'blob' }),
  deleteAttachment: (attachmentId) => axios.delete(`${API_URL}/knowledge/attachments/${attachmentId}`),
};

export const mailboxApi = {
  status: () => axios.get(`${API_URL}/mailbox/status`),
  syncNow: () => axios.post(`${API_URL}/mailbox/sync`),
  list: (params) => axios.get(`${API_URL}/mailbox/emails`, { params }),
  targets: (q) => axios.get(`${API_URL}/mailbox/targets`, { params: { q } }),
  assign: (id, data) => axios.post(`${API_URL}/mailbox/emails/${id}/assign`, data),
  ignore: (id) => axios.post(`${API_URL}/mailbox/emails/${id}/ignore`),
  reopen: (id) => axios.post(`${API_URL}/mailbox/emails/${id}/reopen`),
  remove: (id) => axios.delete(`${API_URL}/mailbox/emails/${id}`),
  rules: () => axios.get(`${API_URL}/mailbox/rules`),
  deleteRule: (address) => axios.delete(`${API_URL}/mailbox/rules/${encodeURIComponent(address)}`),
};

export const customerSystemsApi = {
  list: () => axios.get(`${API_URL}/customer-systems`),
  create: (data) => axios.post(`${API_URL}/customer-systems`, data),
  update: (id, data) => axios.put(`${API_URL}/customer-systems/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/customer-systems/${id}`),
};

export const vendorsApi = {
  list: (search) => axios.get(`${API_URL}/vendors`, { params: { search: search || undefined } }),
  quickAccess: () => axios.get(`${API_URL}/vendors/quick-access`),
  get: (id) => axios.get(`${API_URL}/vendors/${id}`),
  create: (data) => axios.post(`${API_URL}/vendors`, data),
  update: (id, data) => axios.put(`${API_URL}/vendors/${id}`, data),
  uploadLogo: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API_URL}/vendors/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  refreshLogo: (id) => axios.post(`${API_URL}/vendors/${id}/logo/refresh`),
  listSub: (id, kind) => axios.get(`${API_URL}/vendors/${id}/${kind}`),
  addItem: (id, kind, data) => axios.post(`${API_URL}/vendors/${id}/${kind}`, data),
  updateItem: (id, kind, itemId, data) => axios.put(`${API_URL}/vendors/${id}/${kind}/${itemId}`, data),
  deleteItem: (id, kind, itemId) => axios.delete(`${API_URL}/vendors/${id}/${kind}/${itemId}`),
  trackClick: (id, linkId) => axios.post(`${API_URL}/vendors/${id}/links/${linkId}/click`),
  uploadDocument: (id, file, title, category) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || "");
    fd.append("category", category || "");
    return axios.post(`${API_URL}/vendors/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  downloadDocument: (id, docId) => axios.get(`${API_URL}/vendors/${id}/documents/${docId}/download`, { responseType: "blob" }),
  customers: (id) => axios.get(`${API_URL}/vendors/${id}/customers`),
  setCustomerSoftware: (customerId, entries) => axios.put(`${API_URL}/customers/${customerId}/medical-software`, { entries }),
};

// Chat API
export const chatApi = {
  getMessages: (limit = 50, before = null) => 
    axios.get(`${API_URL}/chat/messages`, { params: { limit, before } }),
  sendMessage: (message) => 
    axios.post(`${API_URL}/chat/messages`, { message }),
  updateMessage: (id, message) =>
    axios.put(`${API_URL}/chat/messages/${id}`, { message }),
  deleteMessage: (id) => axios.delete(`${API_URL}/chat/messages/${id}`),
  getUnreadCount: () => axios.get(`${API_URL}/chat/unread-count`),
  markRead: () => axios.post(`${API_URL}/chat/mark-read`),
};

// Web-Push (PWA) API
export const pushApi = {
  getVapidKey: () => axios.get(`${API_URL}/push/vapid-public-key`),
  subscribe: (subscription) => axios.post(`${API_URL}/push/subscribe`, subscription),
  unsubscribe: (endpoint) => axios.post(`${API_URL}/push/unsubscribe`, { endpoint }),
  getStatus: () => axios.get(`${API_URL}/push/status`),
  sendTest: () => axios.post(`${API_URL}/push/test`),
};

// Exchange API
export const exchangeApi = {
  connect: (email, client_id, client_secret, tenant_id, server = "outlook.office365.com") => 
    axios.post(`${API_URL}/exchange/connect`, { email, client_id, client_secret, tenant_id, server }),
  disconnect: () => axios.post(`${API_URL}/exchange/disconnect`),
  getStatus: () => axios.get(`${API_URL}/exchange/status`),
  
  // Email
  sendEmail: (to_recipients, subject, body, cc_recipients = [], is_html = false) =>
    axios.post(`${API_URL}/exchange/send-email`, { to_recipients, subject, body, cc_recipients, is_html }),
  getInbox: (limit = 20) => axios.get(`${API_URL}/exchange/inbox`, { params: { limit } }),
  
  // Calendar
  createCalendarEvent: (subject, start_time, end_time, body = "", location = "", attendee_emails = [], send_invitations = true, recurrence = "none", reminder_minutes = 15, calendar_owner = "") =>
    axios.post(`${API_URL}/exchange/calendar/create`, { 
      subject, start_time, end_time, body, location, attendee_emails, send_invitations, recurrence, reminder_minutes, calendar_owner 
    }),
  updateCalendarEvent: (eventId, data) => axios.put(`${API_URL}/exchange/calendar/${eventId}`, data),
  deleteCalendarEvent: (eventId) => axios.delete(`${API_URL}/exchange/calendar/${eventId}`),
  getCalendarEvents: (days = 7) => axios.get(`${API_URL}/exchange/calendar`, { params: { days } }),
  getAllCalendarEvents: (days = 30) => axios.get(`${API_URL}/exchange/calendar/all`, { params: { days } }),
  getUpcomingEvents: (hours = 24) => axios.get(`${API_URL}/exchange/calendar/upcoming`, { params: { hours } }),
  sendCalendarReminder: (event_subject, event_start, event_location, recipient_email) =>
    axios.post(`${API_URL}/exchange/calendar/send-reminder`, { event_subject, event_start, event_location, recipient_email }),
  getSharedCalendarEvents: (email, days = 30) => axios.get(`${API_URL}/exchange/calendar/shared/${email}`, { params: { days } }),
  addSharedCalendar: (email) => axios.post(`${API_URL}/exchange/calendar/shared/add`, { email }),
  removeSharedCalendar: (email) => axios.post(`${API_URL}/exchange/calendar/shared/remove`, { email }),
  listSharedCalendars: () => axios.get(`${API_URL}/exchange/calendar/shared/list`),
  
  // Contacts
  getContacts: (limit = 100) => axios.get(`${API_URL}/exchange/contacts`, { params: { limit } }),
  syncCustomer: (customerId) => axios.post(`${API_URL}/exchange/contacts/sync-customer/${customerId}`),
  syncAllCustomers: () => axios.post(`${API_URL}/exchange/contacts/sync-all`),
};

// Users API (signatures)
export const usersApi = {
  getSignatures: () => axios.get(`${API_URL}/users/signatures`),
  getMySignature: () => axios.get(`${API_URL}/users/me/signature`),
  setMySignature: (signature) => axios.put(`${API_URL}/users/me/signature`, { signature }),
  getAll: () => axios.get(`${API_URL}/users`),
  create: (data) => axios.post(`${API_URL}/users`, data),
  update: (id, data) => axios.put(`${API_URL}/users/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/users/${id}`),
  invite: (data) => axios.post(`${API_URL}/users/invite`, data),
  resendInvite: (id) => axios.post(`${API_URL}/users/${id}/invite/resend`),
  deactivate: (id) => axios.post(`${API_URL}/users/${id}/deactivate`),
  activate: (id) => axios.post(`${API_URL}/users/${id}/activate`),
  updatePermissions: (id, data) => axios.put(`${API_URL}/users/${id}/permissions`, data),
};

// RBAC-Matrix (Phase A)
export const rbacApi = {
  getMeta: () => axios.get(`${API_URL}/rbac/meta`),
};
export const inviteApi = {
  validate: (token) => axios.get(`${API_URL}/auth/invite/${token}`),
  accept: (token, password) => axios.post(`${API_URL}/auth/invite/accept`, { token, password }),
};

// Teams (Phase 4)
export const teamsApi = {
  getAll: () => axios.get(`${API_URL}/teams`),
  create: (name, description) => axios.post(`${API_URL}/teams`, { name, description }),
  update: (id, name, description) => axios.put(`${API_URL}/teams/${id}`, { name, description }),
  delete: (id) => axios.delete(`${API_URL}/teams/${id}`),
};

// Aufgaben
export const tasksApi = {
  getAll: (params) => axios.get(`${API_URL}/tasks`, { params }),
  getByCustomer: (customerId) => axios.get(`${API_URL}/tasks`, { params: { customer_id: customerId } }),
};

// Activity (Phase 5): Audit-Log, Kommentare, Benachrichtigungen
export const activityApi = {
  getAudit: (entityType, entityId) => axios.get(`${API_URL}/audit/${entityType}/${entityId}`),
  getComments: (entityType, entityId) => axios.get(`${API_URL}/comments/${entityType}/${entityId}`),
  addComment: (entityType, entityId, body, mentions = []) =>
    axios.post(`${API_URL}/comments/${entityType}/${entityId}`, { body, mentions }),
  deleteComment: (commentId) => axios.delete(`${API_URL}/comments/${commentId}`),
  getNotifications: (limit = 50) => axios.get(`${API_URL}/notifications`, { params: { limit } }),
  getUnreadCount: () => axios.get(`${API_URL}/notifications/unread-count`),
  markAllRead: () => axios.post(`${API_URL}/notifications/mark-read`),
  markOneRead: (id) => axios.post(`${API_URL}/notifications/${id}/read`),
};

// Voice command interpretation (LLM)
export const voiceApi = {
  command: (text) => axios.post(`${API_URL}/voice/command`, { text }),
};

// Mailing (contact groups + round emails)
export const mailingApi = {
  getGroups: () => axios.get(`${API_URL}/mailing/groups`),
  createGroup: (name) => axios.post(`${API_URL}/mailing/groups`, { name }),
  updateGroup: (id, name) => axios.put(`${API_URL}/mailing/groups/${id}`, { name }),
  deleteGroup: (id) => axios.delete(`${API_URL}/mailing/groups/${id}`),
  addContact: (groupId, data) => axios.post(`${API_URL}/mailing/groups/${groupId}/contacts`, data),
  updateContact: (groupId, contactId, data) => axios.put(`${API_URL}/mailing/groups/${groupId}/contacts/${contactId}`, data),
  deleteContact: (groupId, contactId) => axios.delete(`${API_URL}/mailing/groups/${groupId}/contacts/${contactId}`),
  importCsv: (groupId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API_URL}/mailing/groups/${groupId}/import`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  previewImport: (groupId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API_URL}/mailing/groups/${groupId}/import/preview`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  confirmImport: (groupId, filename, contacts) =>
    axios.post(`${API_URL}/mailing/groups/${groupId}/import/confirm`, { filename, contacts }),
  send: (groupId, payload) => axios.post(`${API_URL}/mailing/groups/${groupId}/send`, payload),
  getHistory: (groupId) => axios.get(`${API_URL}/mailing/groups/${groupId}/history`),
  sendTest: (payload) => axios.post(`${API_URL}/mailing/send-test`, payload),
  previewHtml: (payload) => axios.post(`${API_URL}/mailing/preview-html`, payload),
  uploadAsset: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API_URL}/mailing/assets`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  aiText: (payload) => axios.post(`${API_URL}/mailing/ai/text`, payload),
  spellcheckBlocks: (payload) => axios.post(`${API_URL}/mailing/ai/spellcheck-blocks`, payload),
  aiImage: (payload) => axios.post(`${API_URL}/mailing/ai/image`, payload),
  getCampaigns: () => axios.get(`${API_URL}/mailing/campaigns`),
  getCampaign: (id) => axios.get(`${API_URL}/mailing/campaigns/${id}`),
  checkLinks: (urls) => axios.post(`${API_URL}/mailing/check-links`, { urls }),
  getTemplates: () => axios.get(`${API_URL}/mailing/templates`),
  templateStats: () => axios.get(`${API_URL}/mailing/templates/stats`),
  createTemplate: (data) => axios.post(`${API_URL}/mailing/templates`, data),
  updateTemplate: (id, data) => axios.put(`${API_URL}/mailing/templates/${id}`, data),
  deleteTemplate: (id) => axios.delete(`${API_URL}/mailing/templates/${id}`),
  duplicateTemplate: (id) => axios.post(`${API_URL}/mailing/templates/${id}/duplicate`),
};

// Praxissuche (practice lead generation)
export const praxisApi = {
  getSpecialties: () => axios.get(`${API_URL}/praxissuche/specialties`),
  createSpecialty: (data) => axios.post(`${API_URL}/praxissuche/specialties`, data),
  updateSpecialty: (id, data) => axios.put(`${API_URL}/praxissuche/specialties/${id}`, data),
  deleteSpecialty: (id) => axios.delete(`${API_URL}/praxissuche/specialties/${id}`),
  geocode: (address) => axios.post(`${API_URL}/praxissuche/geocode`, { address }),
  getRuns: () => axios.get(`${API_URL}/praxissuche/runs`),
  createRun: (data) => axios.post(`${API_URL}/praxissuche/runs`, data),
  deleteRun: (id) => axios.delete(`${API_URL}/praxissuche/runs/${id}`),
  search: (id) => axios.post(`${API_URL}/praxissuche/runs/${id}/search`),
  getLeads: (id, params) => axios.get(`${API_URL}/praxissuche/runs/${id}/leads`, { params }),
  updateLead: (leadId, data) => axios.put(`${API_URL}/praxissuche/leads/${leadId}`, data),
  deleteLead: (leadId) => axios.delete(`${API_URL}/praxissuche/leads/${leadId}`),
  exportCsvUrl: (id) => `${API_URL}/praxissuche/runs/${id}/export.csv`,
  exportXlsxUrl: (id) => `${API_URL}/praxissuche/runs/${id}/export.xlsx`,
  enrich: (id) => axios.post(`${API_URL}/praxissuche/runs/${id}/enrich`),
  convertCustomer: (leadId) => axios.post(`${API_URL}/praxissuche/leads/${leadId}/convert-customer`),
  toMailing: (id, groupId) => axios.post(`${API_URL}/praxissuche/runs/${id}/to-mailing`, { group_id: groupId }),
};

export const praxisPoolApi = {
  summary: () => axios.get(`${API_URL}/praxissuche/pool/summary`),
  counts: () => axios.get(`${API_URL}/praxissuche/pool/counts`),
  search: (data) => axios.post(`${API_URL}/praxissuche/pool/search`, data),
  getLeads: (params) => axios.get(`${API_URL}/praxissuche/pool/leads`, { params }),
  getLead: (leadId) => axios.get(`${API_URL}/praxissuche/pool/leads/by-id/${leadId}`),
  locations: (specialty_key) => axios.get(`${API_URL}/praxissuche/pool/locations`, { params: { specialty_key } }),
  searches: (specialty_key) => axios.get(`${API_URL}/praxissuche/pool/searches`, { params: { specialty_key } }),
  markSeen: (specialty_key) => axios.post(`${API_URL}/praxissuche/pool/mark-seen`, { specialty_key }),
  updateLead: (leadId, data, expectedUpdatedAt) =>
    axios.put(`${API_URL}/praxissuche/pool/leads/${leadId}`, data, expectedUpdatedAt ? { params: { expected_updated_at: expectedUpdatedAt } } : undefined),
  deleteLead: (leadId) => axios.delete(`${API_URL}/praxissuche/pool/leads/${leadId}`),
  deletedLeads: (specialty_key) => axios.get(`${API_URL}/praxissuche/pool/leads/deleted`, { params: { specialty_key } }),
  restoreLead: (leadId) => axios.post(`${API_URL}/praxissuche/pool/leads/${leadId}/restore`),
  enrich: (specialty_key, only_unenriched = false) => axios.post(`${API_URL}/praxissuche/pool/enrich`, { specialty_key, only_unenriched }),
  getJob: (jobId) => axios.get(`${API_URL}/praxissuche/pool/jobs/${jobId}`),
  getActiveJob: () => axios.get(`${API_URL}/praxissuche/pool/jobs/active`),
  incompleteCount: (specialty_key) => axios.get(`${API_URL}/praxissuche/pool/incomplete-count`, { params: { specialty_key } }),
  deleteIncomplete: (specialty_key) => axios.post(`${API_URL}/praxissuche/pool/delete-incomplete`, { specialty_key }),
  deleteAll: (specialty_key) => axios.post(`${API_URL}/praxissuche/pool/delete-all`, { specialty_key }),
  emptyTrash: (specialty_key) => axios.post(`${API_URL}/praxissuche/pool/empty-trash`, { specialty_key }),
  convertCustomer: (leadId) => axios.post(`${API_URL}/praxissuche/pool/leads/${leadId}/convert-customer`),
  toMailing: (data) => axios.post(`${API_URL}/praxissuche/pool/to-mailing`, data),
  exportCsvUrl: (params) => `${API_URL}/praxissuche/pool/export.csv?${new URLSearchParams(params).toString()}`,
  exportXlsxUrl: (params) => `${API_URL}/praxissuche/pool/export.xlsx?${new URLSearchParams(params).toString()}`,
  geocode: (address) => axios.post(`${API_URL}/praxissuche/geocode`, { address }),
  getSpecialties: () => axios.get(`${API_URL}/praxissuche/specialties`),
  nightlyStatus: () => axios.get(`${API_URL}/praxissuche/pool/nightly-status`),
  nightlyRun: () => axios.post(`${API_URL}/praxissuche/pool/nightly-run`),
};

// Generic API instance for custom calls
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Offline support: queue failed mutations on both the global axios instance
// and this `api` instance, then auto-replay them when the connection returns.
attachOfflineInterceptor(axios);
attachOfflineInterceptor(api);

export default api;
